import React, { useEffect, useState } from "react";
import {
  Box,
  Avatar,
  Typography,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  IconButton,
  Alert,
  Snackbar,
} from "@mui/material";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import {
  getDoc,
  doc,
  collection,
  query,
  limit,
  onSnapshot,
  getDocs,
  updateDoc,
  addDoc,
  serverTimestamp,
  where,
} from "firebase/firestore";
import { db, auth } from "../api/firebase";

import MoreVertIcon from "@mui/icons-material/MoreVert";
import Menu from "@mui/material/Menu";
import MenuItem from "@mui/material/MenuItem";

const bloodtypeIcons = {
  A: "/assets/bloodtypes/bloodtype_icon_A.png",
  B: "/assets/bloodtypes/bloodtype_icon_B.png",
  AB: "/assets/bloodtypes/bloodtype_icon_AB.png",
  O: "/assets/bloodtypes/bloodtype_icon_O.png",
  unknown: "/assets/bloodtypes/bloodtype_icon_unknown.png",
};

const GroupMembers = ({ group }) => {
  const [members, setMembers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [editingDDP, setEditingDDP] = useState(null);
  const [openDialog, setOpenDialog] = useState(false);
  const [newDDPText, setNewDDPText] = useState("");
  const [openInfoDialog, setOpenInfoDialog] = useState(false);

  const [openBloodDialog, setOpenBloodDialog] = useState(false);
  const [newBloodtype, setNewBloodtype] = useState("unknown");

  const [anchorEl, setAnchorEl] = useState(null);
  const [menuUserId, setMenuUserId] = useState(null);
  const [menuUserName, setMenuUserName] = useState(null); // ← メンバーの名前もセット

  const [openReportDialog, setOpenReportDialog] = useState(false);
  const [isReportConfirmed, setIsReportConfirmed] = useState(false);

  const [reportReason, setReportReason] = useState("");
  const [openSnackbar, setOpenSnackbar] = useState(false);

  const [reportTarget, setReportTarget] = useState(null);

  const [existingReport, setExistingReport] = useState(null);

  const [reporterName, setReporterName] = useState("");

  const handleMenuClick = async (event, uid, name) => {
    event.stopPropagation();
    setAnchorEl(event.currentTarget);
    setMenuUserId(uid);
    setMenuUserName(name);

    try {
      // 該当ユーザーの通報データをチェック
      const q = query(
        collection(db, "groupReports"),
        where("reportedUserId", "==", uid),
        where("groupId", "==", group.groupId)
      );
      const snapshot = await getDocs(q);

      if (!snapshot.empty) {
        const reportDoc = snapshot.docs[0];
        const reportData = reportDoc.data();
        setExistingReport({ id: reportDoc.id, ...reportData });

        // 🔍 reportedBy からユーザー名取得
        if (reportData.reportedBy) {
          const userDoc = await getDoc(doc(db, "users", reportData.reportedBy));
          if (userDoc.exists()) {
            setReporterName(userDoc.data().name || "不明");
          } else {
            setReporterName("不明");
          }
        } else {
          setReporterName("不明");
        }
      } else {
        setExistingReport(null);
        setReporterName("");
      }
    } catch (err) {
      console.error("通報確認エラー:", err);
      setExistingReport(null);
      setReporterName("");
    }
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
    setMenuUserId(null);
  };

  const fetchMembersData = async () => {
    try {
      const membersDataPromises = group.members.map(async (member) => {
        const userId = member.uid || member.userId;
        if (!userId) return null;

        const userDoc = await getDoc(doc(db, "users", userId));
        if (userDoc.exists()) {
          const userData = userDoc.data();

          const ddpRef = collection(db, `users/${userId}/ddps`);
          const q = query(ddpRef, limit(1));
          const querySnapshot = await new Promise((resolve, reject) => {
            onSnapshot(
              q,
              (snapshot) => {
                if (!snapshot.empty) {
                  resolve(snapshot);
                } else {
                  resolve(null);
                }
              },
              reject
            );
          });

          const ddpData = querySnapshot ? querySnapshot.docs[0].data() : null;
          return { ...userData, ddp: ddpData, uid: userId };
        } else {
          return null;
        }
      });

      const membersData = await Promise.all(membersDataPromises);
      setMembers(membersData.filter((member) => member !== null));
    } catch (err) {
      console.error("Error fetching members data:", err);
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (group && group.members) {
      fetchMembersData();
    } else {
      setError("Group or members data is not provided");
      setLoading(false);
    }
  }, [group]);

  const reportGroupMember = async ({ reason, targetUid, targetName }) => {
    try {
      const user = auth.currentUser;
      if (!user) throw new Error("ログイン情報が見つかりません");

      await addDoc(collection(db, "groupReports"), {
        reportedUserId: targetUid,
        reportedUserName: targetName || "",
        reportedBy: user.uid,
        reportedByName: user.displayName || "",
        reportedAt: serverTimestamp(),
        groupId: group.groupId || "unknown", // ← ✅ ここ！
        reportReason: reason,
      });

      console.log(`✅ 通報内容「${reason}」をFirestoreに保存しました`);
    } catch (error) {
      console.error("❌ 通報保存エラー:", error);
    } finally {
      handleMenuClose(); // ← ここでようやく null に戻してOK
      setReportTarget(null);
      setOpenReportDialog(false);
      setIsReportConfirmed(false);
      setOpenSnackbar(true);
    }
  };

  if (loading) return <div>Loading...</div>;
  if (error) return <div>Error: {error}</div>;

  return (
    <Box>
      <Typography variant="h5">{group.name}</Typography>
      <Typography variant="body1">{group.description}</Typography>
      <Box display="flex" flexDirection="column" gap={2}>
        {members.map((member, index) => (
          <>
            <Accordion
              key={index}
              sx={{
                borderRadius: "20px",
                overflow: "hidden",
                background: "linear-gradient(135deg, #fefefe, #f2f2f2)",
                boxShadow: "0 12px 32px rgba(0,0,0,0.08)",
                transition: "transform 0.3s ease, box-shadow 0.3s ease",
                "&:hover": {
                  transform: "translateY(-4px)",
                  boxShadow: "0 20px 40px rgba(0,0,0,0.12)",
                },
                "&:before": { display: "none" },
              }}
            >
              <AccordionSummary
                expandIcon={<ExpandMoreIcon sx={{ color: "#888" }} />}
                aria-controls={`panel${index}-content`}
                id={`panel${index}-header`}
                sx={{
                  px: 4,
                  py: 3,
                  "& .MuiAccordionSummary-content": {
                    display: "flex",
                    alignItems: "center",
                    gap: 2,
                  },
                  position: "relative", // ← これが重要
                }}
              >
                <Avatar
                  src={member.avatar}
                  alt={member.name}
                  sx={{
                    width: 60,
                    height: 60,
                    border: "3px solid #e0e0e0",
                    boxShadow: "0 4px 8px rgba(0,0,0,0.08)",
                    backgroundColor: "#fff",
                    transition: "0.3s",
                    "&:hover": {
                      borderColor: "#1976d2",
                    },
                  }}
                />
                <Box>
                  <Typography
                    variant="h6"
                    fontWeight="bold"
                    sx={{ color: "#222", letterSpacing: 0.5 }}
                  >
                    {member.name}
                  </Typography>
                  {/* <img
                    src={bloodtypeIcons[member.bloodtype || "unknown"]}
                    alt="血液型アイコン"
                    width={36}
                    height={36}
                    style={{ borderRadius: "50%" }}
                  /> */}
                </Box>

                  {/* 通報ボタン */}
                {member.uid !== auth.currentUser?.uid && (
                  <IconButton
                    onClick={(e) => handleMenuClick(e, member.uid, member.name)}
                    sx={{ position: "absolute", top: 16, right: 16 }}
                  >
                    <MoreVertIcon />
                  </IconButton>
                )}


              </AccordionSummary>

              <AccordionDetails sx={{ px: 4, pb: 3 }}>
                {member.ddp && (
                  <Box>
                    <Box
                      sx={{
                        background: "#f3f6fa",
                        borderLeft: "5px solid #1976d2",
                        borderRadius: "12px",
                        px: 3,
                        py: 2,
                        mb: 2,
                        fontStyle: "italic",
                      }}
                    >
                      <Typography
                        variant="body1"
                        sx={{
                          whiteSpace: "pre-wrap",
                          lineHeight: 1.8,
                          color: "#333",
                        }}
                      >
                        {member.ddp.ddp}
                      </Typography>
                    </Box>

                    {(member.uid || member.userId) ===
                      auth.currentUser?.uid && (
                      <Box display="flex" justifyContent="flex-end">
                        {/* <Button
                          variant="outlined"
                          size="small"
                          sx={{
                            borderRadius: "20px",
                            textTransform: "none",
                            px: 3,
                            mr: 1, // ← DDPボタンとの間隔
                            "&:hover": {
                              backgroundColor: "#fff3e0",
                            },
                          }}
                          onClick={() => {
                            setEditingDDP(member);
                            setNewBloodtype(member.bloodtype || "unknown");
                            setOpenBloodDialog(true);
                          }}
                        >
                          血液型を編集
                        </Button> */}

                        <Button
                          variant="outlined"
                          size="small"
                          sx={{
                            borderRadius: "20px",
                            textTransform: "none",
                            px: 3,
                            "&:hover": {
                              backgroundColor: "#e3f2fd",
                            },
                          }}
                          onClick={() => {
                            setEditingDDP(member);
                            setNewDDPText(member.ddp.ddp || "");
                            setOpenDialog(true);
                          }}
                        >
                          DDPを編集
                        </Button>
                      </Box>
                    )}
                  </Box>
                )}
              </AccordionDetails>
            </Accordion>
          </>
        ))}

        {/* 通報メニュー */}
        <Menu
          anchorEl={anchorEl}
          open={Boolean(anchorEl)}
          onClose={handleMenuClose}
          anchorOrigin={{
            vertical: "top",
            horizontal: "left",
          }}
          transformOrigin={{
            vertical: "top",
            horizontal: "right",
          }}
          PaperProps={{
            sx: {
              mt: 1,
              borderRadius: 2,
              boxShadow: 3,
              minWidth: 280,
            },
          }}
        >
          <MenuItem
            disabled={!!existingReport}
            onClick={() => {
              setReportTarget({
                uid: menuUserId,
                name: menuUserName,
              });
              setOpenReportDialog(true);
            }}
            sx={{ alignItems: "flex-start", py: 1.5 }}
          >
            <Box display="flex" alignItems="flex-start" gap={2}>
              <InfoOutlinedIcon sx={{ mt: 0.5 }} />
              <Box>
                {/* 常に表示 */}
                <Typography variant="body1" fontWeight="bold">
                  グループ活動未参加を連絡
                </Typography>
                <Typography
                  variant="body2"
                  color="text.secondary"
                  sx={{
                    whiteSpace: "normal",
                    wordBreak: "break-word",
                    mb: existingReport ? 1 : 0,
                  }}
                >
                  事務局に{menuUserName}さんの参加状況を報告します
                </Typography>

                {/* 既に報告済みなら表示 */}
                {existingReport && (
                  <>
                    <Typography
                      variant="body2"
                      fontWeight="bold"
                      sx={{ color: "#4caf50", mb: 0.5 }}
                    >
                      ✅{" "}
                      {new Date(
                        existingReport.reportedAt?.toDate?.()
                      ).toLocaleDateString("ja-JP")}{" "}
                      {reporterName || "不明"} が報告済み
                    </Typography>

                    {existingReport.history &&
                      existingReport.history
                        .slice()
                        .sort(
                          (a, b) => b.createdAt?.seconds - a.createdAt?.seconds
                        )
                        .map((h, idx) => (
                          <Typography
                            key={idx}
                            variant="body2"
                            sx={{
                              whiteSpace: "normal",
                              wordBreak: "break-word",
                              fontSize: "0.8rem",
                              color: "#555",
                              ml: 1,
                            }}
                          >
                            📝 {h.text}（{"事務局"} -{" "}
                            {h.createdAt?.toDate
                              ? h.createdAt.toDate().toLocaleDateString("ja-JP")
                              : "不明"}
                            ）
                          </Typography>
                        ))}
                  </>
                )}
              </Box>
            </Box>
          </MenuItem>
        </Menu>
      </Box>

      <Dialog
        open={openDialog}
        onClose={() => setOpenDialog(false)}
        fullWidth
        maxWidth="sm"
      >
        <DialogTitle>
          DDPを編集
          <IconButton
            aria-label="info"
            onClick={() => setOpenInfoDialog(true)}
            sx={{ position: "absolute", right: 16, top: 16 }}
          >
            <InfoOutlinedIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent>
          <TextField
            autoFocus
            margin="dense"
            label="DDP"
            type="text"
            fullWidth
            multiline
            minRows={4}
            maxRows={7}
            value={newDDPText}
            onChange={(e) => setNewDDPText(e.target.value)}
            helperText={`${newDDPText.length} 文字`}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenDialog(false)}>キャンセル</Button>
          <Button
            onClick={async () => {
              if (editingDDP) {
                const userId = editingDDP.uid || editingDDP.userId;
                const ddpRef = collection(db, `users/${userId}/ddps`);
                const snapshot = await getDocs(query(ddpRef, limit(1)));

                if (!snapshot.empty) {
                  const docId = snapshot.docs[0].id;
                  await updateDoc(doc(db, `users/${userId}/ddps/${docId}`), {
                    ddp: newDDPText,
                  });
                }

                setOpenDialog(false);
                fetchMembersData();
              }
            }}
          >
            保存
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={openInfoDialog}
        onClose={() => setOpenInfoDialog(false)}
        fullWidth
        maxWidth="sm"
      >
        <DialogTitle>DDP設定のポイント</DialogTitle>
        <DialogContent>
          <Typography variant="subtitle1" fontWeight="bold" mt={2}>
            🔷 DDPの目的と基本概念
          </Typography>
          <Typography paragraph>
            DDPとは：自分が「どうなりたいか（なりたい姿）」を明確にするための考え方。手段や通過点ではなく、願望のコアを言語化する。
            <br />
            <br />
            "したい"ではなく"なりたい"を考える：例）「宝くじを当てたい」は“手段”、「経済的に安心した暮らしをしたい」が“なりたい”。
            <br />
            <br />
            最終目的は“ありたい”だが、DDPでは“なりたい”にフォーカスする。
          </Typography>

          <Typography variant="subtitle1" fontWeight="bold">
            🔷 DDPを考える上での3つの視点
          </Typography>
          <Typography paragraph>
            Definition（定義）：願いを明確に、具体的にすること。例：「歌手になりたい」ではなく「私が書いた曲で多くの人を感動させたい」。
            <br />
            Direction（方向）：その願いに向けて今後どのように進むかの指針を持つこと。
            <br />
            Purpose（目的）：その願いの本質的な理由。「それをすることで、どう感じたいのか」。
          </Typography>

          <Typography variant="subtitle1" fontWeight="bold">
            🔷 DDPを作るときの重要なポイント
          </Typography>
          <Typography paragraph>
            自己中心的であること：「他人と比較した金持ち」ではなく「自分が心地よく感じられる暮らし」など、自分基準で考える。
            <br />
            時間性を持つこと：「老後が安心したい」など未来全体ではなく、「半年後にどうなっていたいか」など、期間を定める。
            <br />
            手段と目的を混同しないこと：例）「武道館で歌いたい」は他人依存の手段、「自分が納得できる曲を作りたい」が本来の目的。
            <br />
            積み上げ方式で考える：現実的なステップを重ねて最終ゴールに近づくようにする。
          </Typography>

          <Typography variant="subtitle1" fontWeight="bold">
            🔷 願いを叶える鍵は「信じきれるか」
          </Typography>
          <Typography paragraph>
            願いは「信じきれたら」叶うという前提がある。
            <br />
            自分が“思える”ことは、すでにその世界が存在している証拠（量子力学的観点）。
            <br />
            DDPは、「ただの夢」ではなく、「自分が信じられる願い」としての“なりたい姿”を言語化することで、より現実的にその方向へと動いていくためのツールです。
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenInfoDialog(false)}>閉じる</Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={openBloodDialog}
        onClose={() => setOpenBloodDialog(false)}
        fullWidth
        maxWidth="xs"
      >
        <DialogTitle>血液型を編集</DialogTitle>
        <DialogContent>
          <TextField
            select
            fullWidth
            label="血液型"
            value={newBloodtype}
            onChange={(e) => setNewBloodtype(e.target.value)}
            SelectProps={{ native: true }}
            sx={{ mt: 2 }}
          >
            <option value="A">A型</option>
            <option value="B">B型</option>
            <option value="AB">AB型</option>
            <option value="O">O型</option>
            <option value="unknown">未登録</option>
          </TextField>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenBloodDialog(false)}>キャンセル</Button>
          <Button
            onClick={async () => {
              const userId = editingDDP.uid || editingDDP.userId;
              await updateDoc(doc(db, "users", userId), {
                bloodtype: newBloodtype,
              });
              setOpenBloodDialog(false);
              fetchMembersData();
            }}
          >
            保存
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={openReportDialog}
        onClose={() => {
          setOpenReportDialog(false);
          setIsReportConfirmed(false);
        }}
        fullWidth
        maxWidth="xs"
      >
        <DialogTitle>報告の確認</DialogTitle>
        <DialogContent>
          <Typography mb={2}>
            本当に
            <strong>{menuUserName}</strong>
            さんの未参加を報告してもよろしいですか？
          </Typography>
          <Box display="flex" alignItems="center">
            <input
              type="checkbox"
              id="confirmReport"
              checked={isReportConfirmed}
              onChange={(e) => setIsReportConfirmed(e.target.checked)}
            />
            <label htmlFor="confirmReport" style={{ marginLeft: 8 }}>
              この操作は取り消せません。よろしいですか？
            </label>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => {
              setOpenReportDialog(false);
              setIsReportConfirmed(false);
            }}
          >
            キャンセル
          </Button>
          <Button
            onClick={() =>
              reportGroupMember({
                reason: "グループ活動未参加",
                targetUid: reportTarget?.uid,
                targetName: reportTarget?.name,
              })
            }
            disabled={!isReportConfirmed}
            color="error"
            variant="contained"
          >
            報告する
          </Button>
        </DialogActions>
      </Dialog>

      <Snackbar
        open={openSnackbar}
        autoHideDuration={4000}
        onClose={() => setOpenSnackbar(false)}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
      >
        <Alert
          onClose={() => setOpenSnackbar(false)}
          severity="success"
          sx={{ width: "100%" }}
        >
          報告が完了しました。
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default GroupMembers;
